/**
 * TemplateLibraryBrowser Component
 *
 * Full-page browser for the template library.
 * Includes search, category filters, and template cards.
 * Uses the standard NewTemplateBlock component for displaying templates.
 */

import React from 'react';
import NewTemplateBlock from '@components/Templates/NewTemplateBlock';
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  Box,
  Chip,
  IconButton,
  InputBase,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { EDITOR_MAX_WIDTH_WITH_PADDING } from '../constants/layout';
import { useTemplateLibrary } from '../hooks/useTemplateLibrary';
import type { AdminPublicTemplate, AvailableModule } from '../types';

interface TemplateLibraryBrowserProps {
  /** Available public templates */
  publicTemplates: AdminPublicTemplate[];
  /** Template IDs already in user's list */
  excludedTemplateIds: string[];
  /** Available modules for previews */
  availableModules?: AvailableModule[];
  /** Callback to add template */
  onAddTemplate: (templateId: string) => void;
  /** Callback to customize (copy) template */
  onCustomizeTemplate: (template: AdminPublicTemplate) => void;
  /** Callback to close browser */
  onClose: () => void;
}

export const TemplateLibraryBrowser: React.FC<TemplateLibraryBrowserProps> = ({
  publicTemplates,
  excludedTemplateIds,
  availableModules,
  onAddTemplate,
  onCustomizeTemplate,
  onClose,
}) => {
  const theme = useTheme();

  const {
    filteredTemplates,
    searchTerm,
    categoryFilter,
    categories,
    setSearchTerm,
    setCategoryFilter,
    clearFilters,
    hasActiveFilters,
    resultCount,
  } = useTemplateLibrary({
    publicTemplates,
    excludedTemplateIds,
    availableModules,
  });

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: EDITOR_MAX_WIDTH_WITH_PADDING,
        mx: 'auto',
        p: { xs: 2, sm: 3, md: 4 },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            fontWeight={600}
            color="text.primary"
            sx={{ mb: 0.5 }}
          >
            Template Library
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Browse and add pre-built templates to your collection
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ color: 'text.secondary' }}
          aria-label="Close library"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Search */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          px: 2,
          py: 1.5,
          bgcolor: 'background.paper',
          mb: 2,
          transition: theme.transitions.create(['border-color', 'box-shadow'], {
            duration: theme.transitions.duration.short,
          }),
          '&:focus-within': {
            borderColor: 'primary.main',
            boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
          },
        }}
      >
        <SearchIcon sx={{ color: 'text.secondary', mr: 1.5 }} />
        <InputBase
          fullWidth
          placeholder="Search by name, description, or sections..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          inputProps={{
            'aria-label': 'Search templates',
          }}
          sx={{
            '& input::placeholder': {
              color: 'text.secondary',
              opacity: 0.8,
            },
          }}
        />
        {searchTerm && (
          <IconButton
            size="small"
            onClick={() => setSearchTerm('')}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Category Filters */}
      {categories.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            mb: 3,
          }}
        >
          <Chip
            label="All"
            onClick={() => setCategoryFilter(null)}
            variant={categoryFilter === null ? 'filled' : 'outlined'}
            color={categoryFilter === null ? 'primary' : 'default'}
            sx={{ fontWeight: categoryFilter === null ? 600 : 400 }}
          />
          {categories.map(category => (
            <Chip
              key={category}
              label={category}
              onClick={() => setCategoryFilter(category)}
              variant={categoryFilter === category ? 'filled' : 'outlined'}
              color={categoryFilter === category ? 'primary' : 'default'}
              sx={{ fontWeight: categoryFilter === category ? 600 : 400 }}
            />
          ))}
        </Box>
      )}

      {/* Results Count */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {resultCount} template{resultCount !== 1 ? 's' : ''} found
          {hasActiveFilters && (
            <Typography
              component="span"
              variant="body2"
              color="primary"
              sx={{
                ml: 1,
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
              onClick={clearFilters}
            >
              Clear filters
            </Typography>
          )}
        </Typography>
      </Box>

      {/* Templates List */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {filteredTemplates.map(template => (
          <Box
            key={template.template_id}
            sx={{
              flexShrink: 0,
            }}
          >
            <NewTemplateBlock
              template={template}
              availableModules={availableModules}
              handleClick={() => onAddTemplate(template.template_id)}
              handleCopy={() => onCustomizeTemplate(template)}
            />
          </Box>
        ))}

        {/* Empty State */}
        {filteredTemplates.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              No templates found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {hasActiveFilters
                ? 'Try adjusting your search or filters'
                : 'No templates are available at this time'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
